import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2, Bot, User, MessageSquare } from 'lucide-react';
import { useDashboard } from '@/context/DashboardContext';
import { streamPrivateChat } from '@/services/chatApi';
import { renderMarkdownBold } from '@/utils/renderMarkdown';
import { hasMemberAccess } from '@/components/dashboard/MembersOnly';
import { useSiteLang } from '@/components/site/SiteLang';

// Dos asistentes (decisión 08/10/2026): socios (datos reales del CDM) y registrados sin membresía
const QUICK_QUESTIONS_ES = {
  member: [
    '¿Cuánto llevo generado en comisiones y cuánto puedo cobrar ya?',
    '¿Por qué alguna comisión aún no es exigible?',
    '¿Cuántas personas tengo en mi red?',
    '¿Qué estrategias me recomiendas para presentar el club?',
    'Explícame cómo funcionan las aportaciones',
  ],
  guest: [
    '¿Qué incluye cada membresía?',
    '¿Qué es el Bootcamp Broker Digital?',
    '¿Cómo me hago socio?',
    '¿Qué formación ofrece la Academia?',
  ],
};

const QUICK_QUESTIONS_EN = {
  member: [
    'How much have I generated in commissions so far, and how much can I collect now?',
    'Why isn’t one of my commissions payable yet?',
    'How many people are in my network?',
    'What strategies do you recommend for presenting the club?',
    'Explain how contributions work',
  ],
  guest: [
    'What does each membership include?',
    'What is the Digital Broker Bootcamp?',
    'How do I become a member?',
    'What training does the Academy offer?',
  ],
};

// Textos de interfaz (es / en). El servidor responde en el idioma del panel (cabecera X-Lang).
const T = {
  es: {
    quick: QUICK_QUESTIONS_ES,
    greetMember: (name) => `Hola, ${name} 👋\n\nSoy Freedy, tu asistente de socio en Freedom Homes Club. Puedo ayudarte con tus comisiones y tu red (con los datos reales del club), la Academia, los eventos y cualquier duda del panel.\n\n¿En qué te ayudo hoy?`,
    greetGuest: (name) => `Hola, ${name} 👋\n\nSoy Freedy. Te cuento todo sobre Freedom Homes Club: las membresías, la formación, los eventos y cómo hacerte socio.\n\n¿Qué te gustaría saber?`,
    error: 'Ha ocurrido un error. Inténtalo de nuevo.',
    title: 'Freedy · Asistente IA',
    modeMember: 'Modo socio: tus datos del club',
    modeGuest: 'Información del club (sin membresía)',
    badge: 'Asistente IA',
    placeholder: 'Escribe tu pregunta...',
    send: 'Enviar',
    footer: 'Shift+Enter para nueva línea · Las respuestas son informativas, no asesoramiento financiero.',
  },
  en: {
    quick: QUICK_QUESTIONS_EN,
    greetMember: (name) => `Hi${name ? ` ${name}` : ''} 👋\n\nI’m Freedy, your member assistant at Freedom Homes Club. I can help with your commissions and your network (using the club’s real data), the Academy, events and any questions about the dashboard.\n\nHow can I help today?`,
    greetGuest: (name) => `Hi${name ? ` ${name}` : ''} 👋\n\nI’m Freedy. I can tell you all about Freedom Homes Club: the memberships, the training, the events and how to become a member.\n\nWhat would you like to know?`,
    error: 'Something went wrong. Please try again.',
    title: 'Freedy · AI assistant',
    modeMember: 'Member mode: your club data',
    modeGuest: 'Club information (no membership)',
    badge: 'AI assistant',
    placeholder: 'Type your question…',
    send: 'Send',
    footer: 'Shift+Enter for a new line · Answers are for information only and are not financial advice.',
  },
};

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
    >
      {/* Avatar */}
      <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5 ${
        isUser ? 'bg-[#c9a84c]/20' : 'bg-white/[0.06] border border-white/[0.08]'
      }`}>
        {isUser
          ? <User size={13} className="text-[#c9a84c]" />
          : <Bot size={13} className="text-gray-400" />
        }
      </div>

      {/* Bubble */}
      <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
        isUser
          ? 'bg-[#c9a84c] text-[#0a1225] font-medium rounded-tr-sm'
          : 'bg-white/[0.05] border border-white/[0.07] text-gray-200 rounded-tl-sm'
      }`} style={{ whiteSpace: 'pre-wrap' }}>
        {msg.content ? renderMarkdownBold(msg.content) : <span className="opacity-40 animate-pulse">▌</span>}
      </div>
    </motion.div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-3">
      <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center bg-white/[0.06] border border-white/[0.08]">
        <Bot size={13} className="text-gray-400" />
      </div>
      <div className="px-4 py-3 rounded-2xl rounded-tl-sm bg-white/[0.05] border border-white/[0.07]">
        <div className="flex gap-1 items-center">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function BrokerChat() {
  const { user, membership, membershipStatus, isAdmin } = useDashboard();
  const membershipKnown = membershipStatus !== 'idle' && membershipStatus !== 'loading';
  const mode = hasMemberAccess({ user, membership, isAdmin }) ? 'member' : 'guest';
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Build personalized greeting once user is loaded
  const greetingLang = useRef(null);
  useEffect(() => {
    if (user && !started && membershipKnown) {
      setStarted(true);
      greetingLang.current = lang;
      const first = user.name?.split(' ')[0] || '';
      setMessages([{
        role: 'assistant',
        content: mode === 'member' ? t.greetMember(first) : t.greetGuest(first),
      }]);
    }
  }, [user, started, mode, membershipKnown]);

  // Si cambia el idioma antes de empezar a conversar, el saludo se rehace en el idioma nuevo
  useEffect(() => {
    if (!started || greetingLang.current === lang) return;
    greetingLang.current = lang;
    const first = user?.name?.split(' ')[0] || '';
    setMessages((prev) => (prev.length === 1 && prev[0].role === 'assistant'
      ? [{ role: 'assistant', content: mode === 'member' ? t.greetMember(first) : t.greetGuest(first) }]
      : prev));
  }, [lang]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const content = (text || input).trim();
    if (!content || loading) return;

    const userMsg = { role: 'user', content };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    await streamPrivateChat(newMessages, {
      onChunk: (chunk) => {
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'assistant',
            content: updated[updated.length - 1].content + chunk,
          };
          return updated;
        });
      },
      onDone: () => setLoading(false),
      onError: (err) => {
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'assistant',
            content: (typeof err === 'string' ? err : err?.message) || t.error,
          };
          return updated;
        });
        setLoading(false);
      },
    });
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const showQuickQuestions = messages.length <= 1;

  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden flex flex-col" style={{ height: '600px' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.06] bg-white/[0.02]">
        <div className="w-9 h-9 rounded-xl bg-[#c9a84c]/10 border border-[#c9a84c]/20 flex items-center justify-center">
          <MessageSquare size={17} className="text-[#c9a84c]" />
        </div>
        <div>
          <p className="text-white font-semibold text-sm">{t.title}</p>
          <p className="text-gray-400 text-xs" data-testid="chat-mode">{mode === 'member' ? t.modeMember : t.modeGuest}</p>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs text-gray-400">{t.badge}</span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
        {messages.map((msg, i) => (
          <MessageBubble key={i} msg={msg} />
        ))}
        {loading && messages[messages.length - 1]?.content === '' && (
          <TypingIndicator />
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick questions */}
      {showQuickQuestions && messages.length > 0 && (
        <div className="px-5 pb-3 flex flex-wrap gap-1.5">
          {t.quick[mode].map((q) => (
            <button
              key={q}
              onClick={() => sendMessage(q)}
              disabled={loading}
              className="text-xs px-2.5 py-1 rounded-full border border-white/10 text-gray-400 hover:border-[#c9a84c]/40 hover:text-[#c9a84c] transition-colors disabled:opacity-30"
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="px-4 pb-4 pt-2 border-t border-white/[0.06]">
        <div className="flex items-end gap-2 bg-white/[0.04] rounded-xl px-3.5 py-2.5 border border-white/[0.08] focus-within:border-[#c9a84c]/30 transition-colors">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder={t.placeholder}
            disabled={loading}
            rows={1}
            className="flex-1 bg-transparent text-white text-sm placeholder-gray-600 resize-none focus:outline-none"
            style={{ maxHeight: '80px', overflowY: 'auto' }}
          />
          <button
            type="button"
            aria-label={t.send}
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-30 hover:scale-105"
            style={{ backgroundColor: '#c9a84c' }}
          >
            {loading ? (
              <Loader2 size={15} className="animate-spin text-[#0a1225]" />
            ) : (
              <Send size={15} className="text-[#0a1225]" />
            )}
          </button>
        </div>
        <p className="text-center text-[11px] text-gray-500 mt-1.5">
          {t.footer}
        </p>
      </div>
    </div>
  );
}
